/*!
 * Copyright (c) Trimble Inc. All rights reserved
 */

import { Component, EventEmitter, Inject, OnDestroy, OnInit, Output } from '@angular/core';
import { ConfigurationLocationInfo } from '../../../models/configuration-location-model';
import { ConfigurationLocationApplyReturnCodes, ConfigurationLocationPathPicker, ConfigurationLocationReturnCodes } from '../../../models/configuration-location-return-codes';
import { EMPTY, Observable, Subject, Subscription } from 'rxjs';
import { filter, switchMap, take, takeUntil } from 'rxjs/operators';
import {
  CONFIGURATION_LOCATION_FACADE_TOKEN,
  IConfigurationLocationFacade,
} from '../../../../../../data-access/src/lib/domain/facades-models/change-configuration-location-facade';
import { SysqueElectricalState } from '../../../models/sysque-electrical-state';
import { GlobalConstants } from '../../../constants/global-constants';
import { SystemInfo } from '../../../../../../data-access/src/lib/domain/system-info';
import { SysQueStateService } from '../../../../../../utils/src/lib/sysque-electrical-state/sysque-elecrical-state.service';
import { AcrylicSurfaceService } from '../../../acrylic-surface/acrylic-surface.service';
import { AlertStatus, IAlert } from "../../../../../../../shared/ui/src/lib/components/alert/alert-types";

@Component({
  selector: 'sysque-configuration-location',
  templateUrl: './configuration-location.component.html',
  styleUrls: ['./configuration-location.component.scss'],
  standalone: false
})
export class ConfigurationLocationComponent implements OnInit, OnDestroy {
  @Output() close = new EventEmitter();
  public data: ConfigurationLocationInfo = {
    pathLabel: '',
    applyName: '',
    revertName: '',
    linkProject: '',
  };
  public componentTitle = '';
  public linkProject = false;
  public warningMessage: string;
  public alertStatus: AlertStatus = AlertStatus.Warning;
  public alertName: string;
  public alertHeight = 350;
  public nonAlertHeight = 300;
  public isAlertVisible = false;
  public folderName: string = '';
  public dockedState = new SysqueElectricalState();
  public shouldShowMessageLinkedProject = false;

  public get alert(): IAlert {
    return {
      message: this.warningMessage,
      type: this.alertStatus,
      title: this.alertName,
      isDismissable: true,
    };
  }

  private selectedFolderName$: Observable<ConfigurationLocationPathPicker>;
  private selectedFolderNameSub: Subscription;
  private readonly closed$ = new Subject<void>();

  constructor(
    @Inject(CONFIGURATION_LOCATION_FACADE_TOKEN) private configurationLocationFacade: IConfigurationLocationFacade, 
    private sysqueStateService: SysQueStateService, private acrylicSurfaceService: AcrylicSurfaceService
  ) {}

  public ngOnInit(): void {
    this.data.pathLabel = GlobalConstants.PathLabel;
    this.data.applyName = GlobalConstants.ApplyButton;
    this.data.revertName = GlobalConstants.RevertButton;
    this.componentTitle = GlobalConstants.ConfigurationLocationDialogName;
    this.data.linkProject = GlobalConstants.LinkProject;
    const result = this.configurationLocationFacade.getFolderPath();
    result.pipe(take(1)).subscribe((response) => {
      this.folderName = response.path;
      this.linkProject = response.isLinkedProject;
    });

    // Set initial state for sysqueStateService
    this.sysqueStateService.setElectricalState(this.configurationLocationFacade.getDockedState());
    this.sysqueStateService.electricalState$.pipe(takeUntil(this.closed$)).subscribe((state) => {
      this.dockedState = state;
    });
  }

  public ngOnDestroy(): void {
    this.closed$.next();
    this.closed$.complete();
    if (this.selectedFolderNameSub) {
      this.selectedFolderNameSub.unsubscribe();
    }
  }

  public linkConfigurationToProject(isChecked: boolean): void {
    this.linkProject = isChecked;
    if (this.linkProject === true) {
      this.configurationLocationFacade.setTetheredConfiguration(this.folderName);
    }
  }

  public closeWindow(): void {
    if(this.shouldShowMessageLinkedProject){
      this.configurationLocationFacade.updateLocalStorage(GlobalConstants.SHOW_LINKED_PROJECT_MESSAGE, true);
    }
    const response = this.configurationLocationFacade.closeWindow();
    if (response) {
      this.close.emit();
      this.acrylicSurfaceService.hide();
    }
  }

  public onFileSelected(): void {
    this.selectedFolderName$ = this.configurationLocationFacade.folderSelection().pipe(
      filter(response => response.path !== 'initial folder path')
    );
    this.selectedFolderNameSub = this.selectedFolderName$.subscribe((response) => {
      this.alertName = 'Failure';
        switch (response.returnCode) {
          case ConfigurationLocationReturnCodes.DirectoryNotFound: {
            this.showAlert('Directory does not exist.', AlertStatus.Warning);
            break;
          }
          case ConfigurationLocationReturnCodes.DirectoryNoPermission: {
            this.showAlert('Directory requires administrative privileges', AlertStatus.Warning);
            break;
          }
          case ConfigurationLocationReturnCodes.Success: {
            if (response.path != '') {
              this.folderName = response.path;
            }
            break;
          }
        }
    });
  }

  public onDismissClick(): void {
    this.configurationLocationFacade.windowResize(this.nonAlertHeight);
    this.isAlertVisible = false;
    this.warningMessage = '';
  }

  public onRevertClick(): void {
    this.linkProject = false;
    this.configurationLocationFacade.revertConfigurationLocation().pipe(take(1)).subscribe(lastSavedConfigurationPath => {
      this.folderName = lastSavedConfigurationPath;
    })
  }

  public onApplyClick(): void {
    this.configurationLocationFacade.verifyDirectory(this.folderName).pipe(
      take(1),
      switchMap(verifiedPath => {
        this.alertName = 'Failure';
        switch (verifiedPath) { 
          case ConfigurationLocationReturnCodes.DirectoryNotFound:
            this.showAlert('Directory does not exist.', AlertStatus.Warning);
            return EMPTY; 
          case ConfigurationLocationReturnCodes.DirectoryNoPermission:
            this.showAlert('Directory requires administrative privileges', AlertStatus.Warning);
            return EMPTY;
          case ConfigurationLocationReturnCodes.Success:
            return this.configurationLocationFacade.applyNewConfigurationLocation(this.folderName, this.linkProject).pipe(take(1));
          default:
            return EMPTY; 
        }
      })
    ).subscribe(response => {
      if (response.returnCode === ConfigurationLocationApplyReturnCodes.SuccessLinkedProject) {
        this.shouldShowMessageLinkedProject = true;
      }
      this.updateDockState(response.systemsList);
      this.closeWindow();
    });
  }

  public showAlert(messageAlert: string, alertStatus: AlertStatus): void {
    this.configurationLocationFacade.windowResize(this.alertHeight);
    this.isAlertVisible = true;
    this.warningMessage = messageAlert;
    this.alertStatus = alertStatus;
  }

  private updateDockState(systemInfo: SystemInfo[]): void {
    this.dockedState = new SysqueElectricalState();
    this.dockedState.systemList = systemInfo;
    this.configurationLocationFacade.updateLocalStorage(GlobalConstants.SYSQUE_ELECTRICAL_STATE_KEY, this.dockedState);
    this.configurationLocationFacade.updateLocalStorage(GlobalConstants.CONFIGURATION_LOCATION, 'Configuration location changed');
  }
}
